import { Navigate, type RouteObject } from 'react-router';
import {
  Component as Root,
  ErrorBoundary,
  HydrateFallback,
  action as rootAction,
  loader as rootLoader,
} from '../routes/root.js';
import { Component as Home, loader as homeLoader } from '../routes/home.js';
import {
  Component as BecomeAPro,
  action as becomeAProAction,
  loader as becomeAProLoader,
} from '../routes/become-a-pro.js';
import {
  Component as Search,
  ErrorBoundary as SearchErrorBoundary,
  loader as searchLoader,
} from '../routes/search.js';
import {
  Component as Provider,
  ErrorBoundary as ProviderErrorBoundary,
  loader as providerLoader,
} from '../routes/provider.js';
import {
  Component as Legal,
  ErrorBoundary as LegalErrorBoundary,
  loader as legalLoader,
} from '../routes/legal.js';
import { Component as NotFound } from '../routes/not-found.js';
import {
  Component as Account,
  action as accountAction,
  loader as accountLoader,
} from '../routes/account.js';
import {
  Component as AccountPro,
  action as accountProAction,
  loader as accountProLoader,
} from '../routes/account-pro.js';
import {
  Component as AccountPayouts,
  action as accountPayoutsAction,
  loader as accountPayoutsLoader,
} from '../routes/account-payouts.js';
import {
  Component as AccountPayoutsRefresh,
  loader as accountPayoutsRefreshLoader,
} from '../routes/account-payouts-refresh.js';
import {
  Component as AccountBilling,
  action as accountBillingAction,
  loader as accountBillingLoader,
} from '../routes/account-billing.js';
import {
  Component as AccountDocuments,
  loader as accountDocumentsLoader,
} from '../routes/account-documents.js';
import {
  Component as AccountDocument,
  loader as accountDocumentLoader,
} from '../routes/account-document.js';
import {
  Component as Booking,
  action as bookingAction,
  loader as bookingLoader,
} from '../routes/booking.js';
import { Component as Jobs, loader as jobsLoader } from '../routes/jobs.js';
import { Component as JobDetail, action as jobAction, loader as jobLoader } from '../routes/job.js';
import { Component as Feed, loader as feedLoader } from '../routes/feed.js';
import {
  Component as JobNew,
  action as jobNewAction,
  loader as jobNewLoader,
} from '../routes/job-new.js';
import {
  Component as FeedJob,
  action as feedJobAction,
  loader as feedJobLoader,
} from '../routes/feed-job.js';
import { Component as Work, loader as workLoader } from '../routes/work.js';
import {
  Component as WorkJob,
  action as workJobAction,
  loader as workJobLoader,
} from '../routes/work-job.js';
import {
  Component as Start,
  action as startAction,
  loader as startLoader,
} from '../routes/start.js';
import { Component as SignUp, action as signUpAction } from '../routes/signup.js';
import { Component as Login, action as loginAction } from '../routes/login.js';
import { Component as VerifyEmail, action as verifyEmailAction } from '../routes/verify-email.js';
import {
  Component as ResetPassword,
  action as resetPasswordAction,
} from '../routes/reset-password.js';
import {
  Component as ResetPasswordSet,
  action as resetPasswordSetAction,
} from '../routes/reset-password-set.js';
import { LOCALES } from '../i18n/index.js';

/**
 * The route table. A slice adds its routes as children of the `/:lang` layout route, so every page
 * inherits the shell — including the not-found page, which is the `path: '*'` child rather than a
 * sibling.
 *
 * **The language is a path segment; the rest of the path is not translated.** `/es/search`, never
 * `/es/buscar`. The operator's decision, 2026-09-11, and it reverses what ADR-011 §2's page
 * inventory showed — the ADR carries the amendment. A localised segment buys a little Spanish
 * keyword relevance in the URL and costs every route two spellings, two redirects and a lookup
 * table; with SEO deferred until there is something in production, it buys nothing at all.
 *
 * Every entry comes from a module in `src/routes/`, each exporting the route contract and nothing
 * else (ADR-011 R1). The rename at the import is the whole seam: the modules are shaped for
 * `W12-T14`'s framework-mode switch, and this file stays a plain table until then.
 */
export const routes: RouteObject[] = [
  // A bare `/` is a visitor who has not said which language they read. ES is the default and the
  // fallback (`TODO.md` §1, Spain first), so it is a redirect rather than a third rendering of home.
  { path: '/', element: <Navigate to={`/${LOCALES[0]}`} replace /> },
  {
    path: '/:lang',
    Component: Root,
    loader: rootLoader,
    // A layout route with an action: the sign-out control lives in the header, so it posts to the
    // route that renders the header rather than to whichever page happens to be underneath.
    action: rootAction,
    ErrorBoundary,
    HydrateFallback,
    children: [
      { index: true, Component: Home, loader: homeLoader },
      // Not `/es/hazte-profesional`: the language is the only translated segment (Amendment 1).
      {
        path: 'become-a-pro',
        Component: BecomeAPro,
        loader: becomeAProLoader,
        action: becomeAProAction,
      },
      // Its own boundary, not the shell's: a failed search must keep the filter rail, which is the
      // only way a visitor can fix the query that failed.
      {
        path: 'search',
        Component: Search,
        loader: searchLoader,
        ErrorBoundary: SearchErrorBoundary,
      },
      // Its own boundary too, and for a second reason beyond the shell rule: a profile has two
      // failure modes — gone, and broken — and only one of them is an error.
      {
        path: 'pro/:id',
        Component: Provider,
        loader: providerLoader,
        ErrorBoundary: ProviderErrorBoundary,
      },
      // `W2-T09` — the account pages. Untranslated segments, like every other route (Amendment 1):
      // `/es/signup`, never `/es/registro`.
      { path: 'signup', Component: SignUp, action: signUpAction },
      // `W2-T10`: where the header's name goes. Its loader is the one guard in the storefront —
      // no session, no page, so it redirects to the login form rather than rendering an empty one.
      { path: 'account', Component: Account, loader: accountLoader, action: accountAction },
      // `W2-T05` §7 — the provider's own profile, editable.
      {
        path: 'account/pro',
        Component: AccountPro,
        loader: accountProLoader,
        action: accountProAction,
      },
      /**
       * `W5-T01` §4.3 — a provider's payouts: the registration step (`?step=registration`, where
       * `become-a-pro` lands), the account page, and Stripe's return (`?return=`) on one path. Behind
       * the same guard as `/account/pro`, and owned by `agent-money`.
       */
      {
        path: 'account/payouts',
        Component: AccountPayouts,
        loader: accountPayoutsLoader,
        action: accountPayoutsAction,
      },
      // Stripe's `refresh_url`: a fresh link and a redirect to it, so it has no action of its own. A
      // static segment, so it is never read as anything else under `account/payouts`.
      {
        path: 'account/payouts/refresh',
        Component: AccountPayoutsRefresh,
        loader: accountPayoutsRefreshLoader,
      },
      /**
       * `W5-T07` §4.10 — a provider's subscription: the plan and Subscribe, or the subscription with
       * Manage billing and Switch; Checkout's return (`?return=`) and cancel (`?cancelled=`) on the
       * same path. Behind the same guard as `/account/payouts`, and owned by `agent-money`.
       */
      {
        path: 'account/billing',
        Component: AccountBilling,
        loader: accountBillingLoader,
        action: accountBillingAction,
      },
      /**
       * `W5-T09` §4.9 — the caller's provisional receipts and payout statements, and one of them with
       * its PDF. Behind a session like `/account`, and owned by `agent-money`.
       */
      { path: 'account/documents', Component: AccountDocuments, loader: accountDocumentsLoader },
      { path: 'account/documents/:id', Component: AccountDocument, loader: accountDocumentLoader },
      /**
       * `W5-T15` §3.2 — a booking, and where its payment lands: after a confirm
       * (`?return=checkout`), back from Stripe (`?return=stripe`), or opened later. Behind the same
       * guard as `/jobs`, and owned by `agent-money`.
       */
      {
        path: 'bookings/:id',
        Component: Booking,
        loader: bookingLoader,
        action: bookingAction,
      },
      /**
       * `W4-T04` — the client's own jobs, and the quotes on one of them.
       *
       * Behind a session like `/account`, and owned by `agent-jobs` rather than `W12`: ADR-011
       * gives the *storefront* to `agent-ui`, and an authenticated area over a slice's own data is
       * built by the slice that owns it — the split `W2-T09` already made with `/account`.
       */
      { path: 'jobs', Component: Jobs, loader: jobsLoader },
      // `W4-T10`. A static segment outranks `:id` in React Router's ranking, so `new` is never read
      // as a job id — and it is declared first anyway, so the table reads in the order it matches.
      { path: 'jobs/new', Component: JobNew, loader: jobNewLoader, action: jobNewAction },
      { path: 'jobs/:id', Component: JobDetail, loader: jobLoader, action: jobAction },
      /**
       * `W4-T07` — the other side of the same funnel: the jobs a professional could take.
       *
       * Behind a session like `/jobs`, owned by `agent-jobs` on the same rule. Linked from the
       * signed-in header since `W4-T10`, which the operator filed with that edit in it.
       */
      { path: 'feed', Component: Feed, loader: feedLoader },
      // `W4-T10` — one job from the feed, and the quote form on it. It reads the feed rather than a
      // job endpoint because the only job endpoint is the owner's (spec §2.5).
      { path: 'feed/:id', Component: FeedJob, loader: feedJobLoader, action: feedJobAction },
      /**
       * `W4-T09` (E8) — the jobs a professional has won, and Start work on one: the code the client's
       * own phone approves. Behind a session, owned by `agent-jobs` on the same rule as `/feed`.
       */
      { path: 'work', Component: Work, loader: workLoader },
      { path: 'work/:jobId', Component: WorkJob, loader: workJobLoader, action: workJobAction },
      /**
       * `W4-T09` §2.1 — where the professional's QR lands, in the client's browser. The token is in
       * the fragment (`#t=`), never the path, so no server log ever holds it.
       */
      { path: 'start', Component: Start, loader: startLoader, action: startAction },
      { path: 'login', Component: Login, action: loginAction },
      // Where better-auth's emailed link redirects back to, with `?error=<CODE>` when the token is
      // no longer good.
      { path: 'verify-email', Component: VerifyEmail, action: verifyEmailAction },
      { path: 'reset-password', Component: ResetPassword, action: resetPasswordAction },
      // Two segments rather than a `:token` param: better-auth's own callback consumes the token
      // and hands it back as a query parameter, so there is nothing in the path to name.
      { path: 'reset-password/set', Component: ResetPasswordSet, action: resetPasswordSetAction },
      {
        path: 'legal/:doc',
        Component: Legal,
        loader: legalLoader,
        ErrorBoundary: LegalErrorBoundary,
      },
      { path: '*', Component: NotFound },
    ],
  },
  // No trailing catch-all: `/:lang` already matches any first segment, and its loader throws a 404
  // for one that is not a language. A `path: '*'` here would be unreachable, which is worse than
  // absent — an unreachable route reads as handled.
];
